import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  GROSS_PROFIT_CHART,
  REPORTS_BASE_URL,
  REPORTS_GROSS_PROFIT_API,
  IS_PY_GRAPHS,
} from "../../../../Constants/Config";
import { useAuthDetails } from "../../../../Common/cookiesHelper";

const initialState = {
  loading: true,
  grossProfitData: [],
  totalGrossProfit: 0,
  previousWeekGrossProfit: 0,
  error: "",
  isCancel: false,
};
let controller = null;

export const getGrossProfit = createAsyncThunk(
  "grossProfitChart/getGrossProfit.",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController instance for the current request
    controller = new AbortController();
    try {
      const { token, ...otherUserData } = userTypeData;

      const requestData = { ...(data || {}) };
      const activeType =
        requestData?.activeType || requestData?.view_by || requestData?.date_type;
      const isWeek = String(activeType || "").toLowerCase() === "week";

      const dateInYYYYMMDD = (date) => {
        const year = date?.getFullYear();
        const month = String(date?.getMonth() + 1)?.padStart(2, "0");
        const day = String(date?.getDate())?.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      const parseLocalYMD = (ymd) => {
        const parts = String(ymd || "").split("-");
        if (parts.length !== 3) return null;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const d = Number(parts[2]);
        if (!y || !m || !d) return null;
        return new Date(y, m - 1, d);
      };

      const getLocalTodayYMD = () => {
        const now = new Date();
        return dateInYYYYMMDD(
          new Date(now.getFullYear(), now.getMonth(), now.getDate())
        );
      };

      const getWeekStartMonday = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = d.getDay(); // 0=Sun
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
      };

      // ===== WEEK LOGIC =====
      if (isWeek) {
        const shouldUseToday = requestData?.isViewingCurrentPeriod === true;
        const anchorYMD = shouldUseToday
          ? getLocalTodayYMD()
          : (requestData?.end_date || requestData?.start_date);
        const anchorDate = parseLocalYMD(anchorYMD) || new Date();

        const weekStart = getWeekStartMonday(anchorDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const result = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + i);
          const formatted = dateInYYYYMMDD(d);
          result.push(`${formatted}_${formatted}`);
        }

        if (shouldUseToday) {
          requestData.start_date = dateInYYYYMMDD(weekStart);
          requestData.end_date = dateInYYYYMMDD(weekEnd);
        } else {
          requestData.start_date = dateInYYYYMMDD(weekStart);
          requestData.end_date = dateInYYYYMMDD(weekEnd);
        }

        requestData.date_range = result.join(",");
        delete requestData.new_date_range;
      }
      const apiUrl =
        IS_PY_GRAPHS === true || IS_PY_GRAPHS === "true"
          ? REPORTS_BASE_URL + REPORTS_GROSS_PROFIT_API
          : BASE_URL + GROSS_PROFIT_CHART;

      const response = await axios.post(
        apiUrl,
        { ...requestData, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );



      let total_gross_profit = 0;

      if (isWeek) {
        const resultArray = response?.data?.gross_profit_data || [];
        console.log("resultArray", resultArray);
        let total = 0;
        if (Array.isArray(resultArray)) {
          resultArray.forEach((row) => {
            total += +row?.gross_profit || 0;
          });
        }

        // const filter_revenue_data = dateKeys.map((d) => ({
        //   total_revenue: parseFloat((total || 0).toFixed(2)),
        //   sale_count: 0,
        // }));
        total_gross_profit = total;
        console.log("total_gross_profit", total_gross_profit);
        return {
          ...response?.data,
          sale_count_data: resultArray,
          total_gross_profit: total_gross_profit,
        };
      }
      total_gross_profit =
        response?.data?.total_gross_profit ?? response?.data?.gross_profit_data ?? 0;

      return {
        ...response?.data,
        total_gross_profit: total_gross_profit,
      };

      // console.log("gross profit api: ", response);
      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") {
        return rejectWithValue({ isCanceled: true }); // Mark as canceled
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const grossProfitChartSlice = createSlice({
  name: "grossProfitChart",
  initialState,
  reducers: {
    clearGrossProfitChart: (state, action) => {
      state.loading = true;
      state.grossProfitData = [];
      state.totalGrossProfit = 0;
      state.previousWeekGrossProfit = 0;
      state.error = "";
      state.isCancel = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGrossProfit.pending, (state) => {
      state.loading = true;
      state.isCancel = false;
    });
    builder.addCase(getGrossProfit.fulfilled, (state, action) => {
      state.loading = false;
      state.grossProfitData = action.payload?.gross_profit_data || [];
      state.totalGrossProfit = action.payload?.total_gross_profit || 0;
      state.previousWeekGrossProfit = action.payload?.previous_gross_profit || 0;
      state.error = "";
      state.isCancel = false;
    });
    builder.addCase(getGrossProfit.rejected, (state, action) => {
      state.loading = false;
      state.grossProfitData = [];
      state.totalGrossProfit = 0;
      state.previousWeekGrossProfit = 0;
      state.error = action.error.message;
      state.isCancel = action.payload.isCanceled;
    });
  },
});

export const { clearGrossProfitChart } = grossProfitChartSlice.actions;
export default grossProfitChartSlice.reducer;
